/**
 * Web shell entry point: shell-specific wiring only.
 *
 * Everything reusable lives in `demo/viewer-core` and is shared verbatim with
 * the Tauri shell — the `DemoApp` controller, the renderer, the stylesheet and
 * the attribution panel. What is left here is what actually differs about a
 * browser: bytes arrive through an `<input type="file">`, and the `MeshSource`
 * is the plain `WasmMeshSource` with its `web-wasm` label.
 */

import "@ifc-engine-core/viewer-core/viewer.css";
// Web-shell-only: the desktop-download card. Kept out of the shared stylesheet
// so the desktop app does not ship rules for a card advertising itself.
import "./download.css";

import { DemoApp, WasmMeshSource, toEngineError } from "@ifc-engine-core/viewer-core";

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (found === null) {
    throw new Error(`missing element #${id}`);
  }
  return found as T;
}

const source = new WasmMeshSource();
// Advisory only — measured in Node (linear memory ≈ 7.8× input bytes, kept
// under 0.75 GiB with 2× headroom); browsers are not measured yet, so the app
// warns and continues rather than refusing. See demo/NOTES.md.
const PROVISIONAL_INPUT_LIMIT_BYTES = 102_754_831;
const app = new DemoApp({
  source,
  inputLimitBytes: PROVISIONAL_INPUT_LIMIT_BYTES,
  // The source-plan projection remains an engine/test candidate, but its
  // current UI is not yet useful enough to publish. Avoid both the panel and
  // its per-model projection work until the drawing-quality lane is ready.
  enablePlanProjection: false,
});

const input = el<HTMLInputElement>("file-input");
input.addEventListener("change", () => {
  const files = input.files;
  if (files === null || files.length === 0) {
    return;
  }
  const file = files[0];
  app.clearFixtureSelection();
  void app.openBlob(file.name, file);
  // Allow re-selecting the same file after a failure.
  input.value = "";
});

// Fetch and instantiate the WASM module while the user is still choosing.
void source.preload().catch((cause: unknown) => {
  app.fail(toEngineError("wasm_init", cause));
});
